/**
 * WHICH REPORTS *THIS CLIENT* FILES — ON THE COMPANY RECORD, WHERE THE CLIENT IS ALREADY IN CONTEXT
 * ================================================================================================
 *
 * The Owner, 2026-10-29: *"אל תפעיל כלל אחיד לכל הארגונים"*, and *"עם מקור, תחולה והגדרות הנדרשות
 * לכל חברה"*.
 *
 * This component is the second half of that sentence. The office's rules live on their own screen;
 * which of them a given client actually files is a property of the client, and it belongs on the
 * client's record rather than on a settings page with a company picker. A picker would have made
 * "which client am I deciding about" a thing a person has to keep in their head while the record
 * they are reading says something else.
 *
 * ── THE LIST INCLUDES WHAT THE OFFICE HAS *NOT* DECIDED, AND THAT IS THE POINT ─────────────────
 *
 * The server returns every rule the office has, with this client's answer beside it — and for a
 * new client that answer is `isEnabled: false` on every row, because **no setting row exists**. A
 * surface that listed only the decisions already made would show an empty panel for exactly the
 * client who needs attention, and an office would have no way to see what it had skipped.
 *
 * ── AND NOTHING HERE CLAIMS A REMINDER WILL ARRIVE ─────────────────────────────────────────────
 *
 * Marking a client does not mean a reminder appears. The rule must also be ACTIVE, which requires
 * it to be verified, which requires a day and a source. When the rule is not active this panel says
 * so on the row (`deadlines.company.ruleNotActive`) rather than letting a green mark imply
 * something the scheduler will not do — the same discipline as *"אין להציג שמירת הגדרה כחיבור
 * פעיל"*.
 */

import { useCallback, useState, type JSX } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { StateBlock } from './StateBlock';
import { classifyActionError, type ActionFailure } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import type { CompanyReminderSettingView } from '../api/contract';

const PERIOD_LABEL: Readonly<Record<string, TextKey>> = {
  monthly: 'deadlines.period.monthly',
  bi_monthly: 'deadlines.period.bi_monthly',
  quarterly: 'deadlines.period.quarterly',
  annual: 'deadlines.period.annual',
};

export interface CompanyReminderSettingsProps {
  /** The client this is about. It travels in the ADDRESS; there is no body field for it. */
  readonly companyUid: string;
}

export function CompanyReminderSettings({ companyUid }: CompanyReminderSettingsProps): JSX.Element {
  const { t } = useLocale();
  /*
   * `useCallback` keyed on the company, because `useServerData` has the reader in its effect's
   * dependency list: a new identity every render is a fetch every render, and an unkeyed one would
   * show the previous client's answers after moving between records.
   */
  const read = useCallback(
    () => callOperation('companies.reminder_setting.list', { params: { company_uid: companyUid } }),
    [companyUid],
  );
  const current = useServerData(read);

  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [note, setNote] = useState('');

  function decide(row: CompanyReminderSettingView, isEnabled: boolean): void {
    setSending(true);
    setFailure(null);
    setSaved(false);
    void callOperation('companies.reminder_setting.put', {
      params: { company_uid: companyUid },
      /*
       * THE OVERRIDES ARE SENT AS THEY ARE, not re-derived. A null means "as the office rule says"
       * and the row already carries whatever this client's answer was, so a toggle does not quietly
       * discard a period or a day somebody set for this client earlier.
       */
      body: {
        ruleUid: row.ruleUid,
        isEnabled,
        periodKind: row.periodKind,
        dueDayOfPeriod: row.dueDayOfPeriod,
        dueMonthOffset: row.dueMonthOffset,
        leadDays: row.leadDays,
        settingsNote: noteFor === row.ruleUid ? (note.trim() === '' ? null : note.trim()) : row.settingsNote,
      },
    })
      .then(() => {
        setSaved(true);
        setNoteFor(null);
        setNote('');
        current.reload();
      })
      .catch((error: unknown) => {
        setFailure(classifyActionError(error));
      })
      .finally(() => {
        setSending(false);
      });
  }

  if (current.result.state === 'loading') {
    return <StateBlock titleHe={t('deadlines.company.title')} bodyHe={t('deadlines.company.lead')} />;
  }
  if (current.result.state === 'failed') {
    /*
     * A FAILED READ IS NOT AN EMPTY DECISION. Rendering the list as "nothing is marked" would
     * invite an operator to mark a client whose real settings nobody managed to read.
     */
    return (
      <section className="tile">
        <h3 className="breakdown__title">{t('deadlines.company.title')}</h3>
        <ActionFailureNotice failure={current.result.failure} />
      </section>
    );
  }

  const { settings } = current.result.data;

  return (
    <>
      <p className="screen-lead">{t('deadlines.company.lead')}</p>
      <p className="form__note">{t('deadlines.company.overrideHint')}</p>

      {saved ? (
        <StateBlock
          titleHe={t('deadlines.company.title')}
          bodyHe={t('deadlines.company.saved')}
        />
      ) : null}
      {failure === null ? null : <ActionFailureNotice failure={failure} />}

      {settings.length === 0 ? (
        <StateBlock
          titleHe={t('deadlines.company.title')}
          bodyHe={t('deadlines.empty')}
          tone="empty"
        />
      ) : (
        <ul className="list list--items">
          {settings.map((row) => (
            <li key={row.ruleUid} className="list__row">
              <div className="list__main">
                <h4 className="list__title">{row.displayNameHe}</h4>
                <span className="chip-row">
                  <span className="chip">
                    {t(row.isEnabled ? 'deadlines.company.enabled' : 'deadlines.company.disabled')}
                  </span>
                  {row.periodKind === null ? null : (
                    <span className="chip">
                      {`${t('deadlines.company.override')}: ${t(PERIOD_LABEL[row.periodKind] ?? 'deadlines.field.period')}`}
                    </span>
                  )}
                </span>
                {row.isEnabled && !row.ruleIsActive ? (
                  <p className="list__meta">{t('deadlines.company.ruleNotActive')}</p>
                ) : null}
                {row.enabledAtUtc === null ? null : (
                  <p className="list__meta" dir="ltr">
                    {`${t('deadlines.company.enabledAt')}: ${row.enabledAtUtc.slice(0, 10)}`}
                  </p>
                )}
                {row.settingsNote === null ? null : (
                  <p className="list__meta">{row.settingsNote}</p>
                )}
              </div>

              <div className="actions-row">
                <button
                  type="button"
                  className="btn btn--quiet"
                  disabled={sending}
                  onClick={() => {
                    decide(row, !row.isEnabled);
                  }}
                >
                  {t(
                    row.isEnabled ? 'deadlines.company.disable' : 'deadlines.company.enable',
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn--quiet"
                  onClick={() => {
                    setNoteFor(noteFor === row.ruleUid ? null : row.ruleUid);
                    setNote(row.settingsNote ?? '');
                  }}
                >
                  {t('deadlines.company.note')}
                </button>
              </div>

              {noteFor === row.ruleUid ? (
                <label className="field">
                  <span className="field__label">{t('deadlines.company.note')}</span>
                  <textarea
                    className="field__input"
                    rows={2}
                    maxLength={400}
                    value={note}
                    onChange={(event) => {
                      setNote(event.target.value);
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={sending}
                    onClick={() => {
                      // Saving the note alone must not change the decision, so the row's own
                      // `isEnabled` is sent back unchanged.
                      decide(row, row.isEnabled);
                    }}
                  >
                    {t('deadlines.save')}
                  </button>
                </label>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
